import { useSelector } from "react-redux"
import { useState, useEffect } from "react"
import _ from "lodash"
import getAssignmentsForAddOnPatients from "./getAssignmentsForAddOnPatients"
import getAssignmentsForRemovedPatients from "./getAssignmentsForRemovedPatients"
import { useAcuityTable } from "../useAcuityTable"
import { usePermissionsList } from "../queries/usePermissions"
import {
	PatientReassignmentError,
	PatientNotFoundError,
	NurseNotFoundError,
	ChairNotFoundError
} from "./errors"
import getAddOnPatients from "./getAddOnPatients"
import useLogging from "../useLogging"
import useFetchSettings from "../useFetchSettings"

/**
 * This hook, calculates necessary reassignments for newly added patients
 * and for nurses with low acuity due to cancellations or no-shows
 *
 * @returns {
 * reassignments: {
 * 	patientId: number,
 * 	newNurseId: number,
 * 	newChairId: number
 * }[] | null,
 * unassignedPatients: Patient[] | null
 * error: {message: string, patientId: number| null, type: "addOnError" | "removedPatientsError"} | null
 * }
 */

export const usePatientReassignment = () => {
	const todaySchedule = useSelector(state => state.schedule)
	const sandbox = useSelector(state => state.sandbox.isSandboxMode)
	const { settingsData: settings } = useFetchSettings()

	const isTomorrowMode = useSelector(state => state.tomorrow.isTomorrowMode)


	const { acuityTable } = useAcuityTable()
	const logging = useLogging()

	const [reassignmentsForAddOnPatients, setReassignmentsForAddOnPatients] = useState([])
	const [unassignedPatientsForAddOnPatients, setUnassignedPatientsForAddOnPatients] = useState([])
	const [addOnError, setAddOnError] = useState(null)

	const permissionsData = usePermissionsList()

	useEffect(() => {
		if (sandbox || isTomorrowMode) {
			setReassignmentsForAddOnPatients([])
			setUnassignedPatientsForAddOnPatients([])

			return
		}
		if (!acuityTable || !settings) {
			return
		}
		if (!permissionsData.includes("write:schedules")) {
			return
		}
		let unassignedPatients
		try {
			unassignedPatients = getAddOnPatients(todaySchedule)
			setUnassignedPatientsForAddOnPatients(unassignedPatients)
		} catch (error) {
			logging.error("usePatientReassignment - Error getting add on patients:", error)
			setReassignmentsForAddOnPatients([])
			setUnassignedPatientsForAddOnPatients([])
			return
		}

		try {
			if (unassignedPatients.length === 0) {
				setReassignmentsForAddOnPatients([])
			}
			const result = getAssignmentsForAddOnPatients(
				todaySchedule,
				acuityTable,
				_.cloneDeep(unassignedPatients),
				settings,
				sandbox,
				logging,
				isTomorrowMode
			)

			setReassignmentsForAddOnPatients(result.reassignments)
			setUnassignedPatientsForAddOnPatients(unassignedPatients)
			setAddOnError(null)
		} catch (error) {
			// TODO: we should just be more specific about the type of error?
			logging.error("usePatientReassignment - Error getting assignments for add on patients:", error)
			if (
				error instanceof NurseNotFoundError ||
				error instanceof ChairNotFoundError ||
				error instanceof PatientNotFoundError
			) {
				setAddOnError({
					message: "An error occurred while calculating reassignments for added patients"
				})
			} else if (error instanceof PatientReassignmentError) {
				setAddOnError({
					message: error.message,
					patientId: error.patientId
				})
			} else {
				setAddOnError({
					message:
						"An unknown error occurred while calculating reassignments for added patients",
					patientId: null
				})
			}
		}
		// `logging` is a fresh object each render (useLogging returns an
		// inline object); adding it would re-run the effect on every render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [acuityTable, settings, todaySchedule, sandbox, permissionsData, isTomorrowMode])

	const [reassignmentsForRemovedPatients, setReassignmentsForRemovedPatients] = useState([])
	const [removedPatientsToConsider, setRemovedPatientsToConsider] = useState([])
	const [removedPatientsError, setRemovedPatientsError] = useState(null)

	useEffect(() => {
		if (sandbox || isTomorrowMode) {
			setReassignmentsForRemovedPatients([])
			setRemovedPatientsToConsider([])
			return
		}
		if (!acuityTable || !settings) {
			return
		}
		if (!permissionsData.includes("write:schedules")) {
			return
		}
		if (addOnError || reassignmentsForAddOnPatients.length > 0) {
			setRemovedPatientsError(null)
			setReassignmentsForRemovedPatients([])
			setRemovedPatientsToConsider([])
			return
		}

		try {
			const result = getAssignmentsForRemovedPatients(
				todaySchedule,
				settings,
				sandbox,
				isTomorrowMode
			)
			setReassignmentsForRemovedPatients(result.reassignments)
			setRemovedPatientsToConsider(result.removedPatientsToConsider)
		} catch (error) {
			logging.error("usePatientReassignment - Error getting assignments for removed patients:", error)
			if (error instanceof NurseNotFoundError || error instanceof ChairNotFoundError) {
				setRemovedPatientsError({
					message:
						"An error occurred while calculating reassignments for removed patients"
				})
			} else if (error instanceof PatientReassignmentError) {
				setRemovedPatientsError({
					message: error.message,
					patientId: error.patientId
				})
			} else {
				setRemovedPatientsError({
					message:
						"An unknown error occurred while calculating reassignments for removed patients",
					patientId: null
				})
			}
		}
		// `logging` is a fresh object each render (useLogging returns an
		// inline object); adding it would re-run the effect on every render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		todaySchedule,
		reassignmentsForAddOnPatients,
		addOnError,
		acuityTable,
		settings,
		permissionsData,
		sandbox,
		isTomorrowMode
	])

	let error = null
	if (addOnError) {
		error = {
			...addOnError,
			type: "addOnError"
		}
	} else if (removedPatientsError) {
		error = {
			...removedPatientsError,
			type: "removedPatientsError"
		}
	}



	return {
		reassignmentsForAddOnPatients,
		unassignedPatientsForAddOnPatients,
		reassignmentsForRemovedPatients,
		removedPatientsToConsider,
		error
	}
}
